import React, { useEffect, useState, useCallback } from 'react';
import { useTheme, themes } from '../context/ThemeContext';
import Modal from '../components/Modal';
import WindowsScrollArrows from '../components/WindowsScrollArrows';
import MenuItemImageUpload from '../components/MenuItemImageUpload';
import { IMAGE_GATEWAY_URL } from '../config/images';

interface CategoryLayoutThemeProps<T extends { id: number; name: string }> {
  title: string;
  headerActions?: React.ReactNode; // New prop for header actions
  categories: T[];
  renderItems: (category: T) => React.ReactNode;
  renderSidebarFooter?: React.ReactNode;
  renderSidebarItem?: (category: T, isSelected: boolean) => React.ReactNode; // isSelected was already added
  onAddCategory?: (newCategory: { name: string; description: string; imageKey?: string }) => void; // Optional: if adding category is part of this component
  vendorId?: number; // For image upload in category creation
  // Add new props for Add Item functionality
  showAddItemForms?: Record<number, boolean>;
  onAddItemClick?: (categoryId: number) => void;
  isCustomer?: boolean; // Add this prop
  hideSidebar?: boolean; // New prop to control sidebar visibility
  sidebarTitle?: string; // New prop for dynamic sidebar title
  compactHeader?: boolean; // New prop to compact header spacing and title size
  disableMinHeight?: boolean; // New prop to disable minimum height calculation
}

/**
 * A reusable themed layout component with a responsive category pane (sidebar/horizontal scroller)
 * and an item display area. Includes a dark mode toggle.
 */
function CardLayoutTheme<T extends { id: number; name: string }>(
  props: CategoryLayoutThemeProps<T>
) {
  const {
    title,
    headerActions,
    categories,
    renderItems,
    onAddCategory,
    onAddItemClick,
    hideSidebar = false, // Default to showing sidebar
    sidebarTitle = 'Categories', // Default value if not provided
    compactHeader = false,
    disableMinHeight = false,
    vendorId
  } = props;

  const { theme, currentThemeName } = useTheme() || { theme: themes.light, currentThemeName: 'light' }; // Fallback to light theme if undefined
  const isModernTheme = currentThemeName === 'modernDashboard';
  
  // Category emoji mapping for modern theme
  const getCategoryEmoji = (name: string): string => {
    const lower = name.toLowerCase();
    if (lower.includes('appetizer') || lower.includes('starter')) return '🥗';
    if (lower.includes('beverage') || lower.includes('drink')) return '🥤';
    if (lower.includes('chinese')) return '🥡';
    if (lower.includes('indian') || lower.includes('curry')) return '🍛';
    if (lower.includes('italian') || lower.includes('pasta') || lower.includes('pizza')) return '🍕';
    if (lower.includes('dessert') || lower.includes('sweet')) return '🍰';
    if (lower.includes('breakfast')) return '🍳';
    if (lower.includes('soup')) return '🍜';
    if (lower.includes('salad')) return '🥗';
    if (lower.includes('rice') || lower.includes('biryani')) return '🍚';
    if (lower.includes('chicken')) return '🍗';
    if (lower.includes('seafood') || lower.includes('fish')) return '🐟';
    if (lower.includes('veg')) return '🥦';
    if (lower.includes('combo') || lower.includes('meal')) return '🍱';
    if (lower.includes('snack')) return '🍿';
    if (lower.includes('burger')) return '🍔';
    if (lower.includes('sandwich') || lower.includes('wrap')) return '🥪';
    if (lower.includes('noodle')) return '🍝';
    if (lower.includes('tandoor') || lower.includes('grill')) return '🔥';
    if (lower.includes('bread') || lower.includes('roti') || lower.includes('naan')) return '🫓';
    if (lower.includes('special') || lower.includes('chef')) return '⭐';
    if (lower.includes('mocktail') || lower.includes('cocktail')) return '🍹';
    if (lower.includes('coffee') || lower.includes('tea')) return '☕';
    return '🍽️';
  };

  // Remember selection only in-memory (per page session). This resets on hard refresh.
  const pathKey = typeof window !== 'undefined' ? window.location.pathname : 'default';
  // Module-level cache shared across component mounts within this session
   
  const memoryStore = (globalThis as any).__cardLayoutCategoryMemory__ || ((globalThis as any).__cardLayoutCategoryMemory__ = {});

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [newCategory, setNewCategory] = useState<{ name: string; description: string }>({ name: '', description: '' });
  const [categoryImageKey, setCategoryImageKey] = useState<string | null>(null);
  const [categoryImageUrl, setCategoryImageUrl] = useState<string | null>(null);

  const [isAddCategoryModalOpen, setAddCategoryModalOpen] = useState(false);

  const handleCategoryImageUploaded = useCallback((key: string) => {
    setCategoryImageKey(key);
    setCategoryImageUrl(`${IMAGE_GATEWAY_URL}/${key}`);
  }, []);

  const handleCategoryImageRemoved = useCallback(() => {
    setCategoryImageKey(null);
    setCategoryImageUrl(null);
  }, []);

  const handleAddCategory = (newCategory: { name: string; description: string }) => {
    if (onAddCategory) {
      onAddCategory({
        ...newCategory,
        imageKey: categoryImageKey || undefined,
      });
    }
    setCategoryImageKey(null);
    setCategoryImageUrl(null);
  };

  // Restore or keep the selected category when the category list changes
  useEffect(() => {
    if (!categories || categories.length === 0) {
      setSelectedCategoryId(null);
      return;
    }

    // If a remembered selection exists (e.g., parent requested a new focus), honor it
    const rememberedId = memoryStore[pathKey] as number | null | undefined;
    if (rememberedId && rememberedId !== selectedCategoryId && categories.some(c => c.id === rememberedId)) {
      setSelectedCategoryId(rememberedId);
      return;
    }

    // If current selection is valid, keep it
    if (selectedCategoryId && categories.some(c => c.id === selectedCategoryId)) {
      return;
    }

    // Fallback to the first category
    setSelectedCategoryId(categories[0].id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  // Save selection whenever it changes (in-memory only)
  useEffect(() => {
    if (selectedCategoryId != null) {
      memoryStore[pathKey] = selectedCategoryId;
    }
  }, [selectedCategoryId, pathKey, memoryStore]);

  const selectedCategory = categories?.find(c => c.id === selectedCategoryId);

  return (
    <div className="w-full transition-colors duration-300 tablet-full-width">
      <div className={`main-content-wrapper ${compactHeader ? 'pt-2 pb-14 md:pb-4' : 'py-8 pb-16 md:pb-8'} flex flex-col ${disableMinHeight ? '' : 'min-h-[calc(100vh-12rem)]'} tablet-main-wrapper`}
           style={{ backgroundColor: theme.background }}>
        {/* Header */}
        <header className={`${compactHeader ? 'mb-4' : 'mb-6'} flex items-center justify-between gap-2`}>
          {title && <h1 className={`${compactHeader ? 'text-sm md:text-lg' : 'text-3xl'} font-bold flex-shrink-0`} style={{ color: theme.text }}>{title}</h1>}
          <div className="flex items-center gap-2 flex-shrink-0">
            {headerActions}
          </div>
        </header>

        <div className="flex flex-col md:flex-row flex-1 gap-y-6 md:gap-x-6 overflow-hidden min-h-0 tablet-content-area">
          {/* Categories - Only show if not hidden */}
          {!hideSidebar && (
            <>
              {/* Categories - Horizontal Scroller for Mobile/Tablet */}
              <div className="md:hidden mb-2 w-full mobile-category-section">
                <WindowsScrollArrows>
                  <div className="flex overflow-x-auto space-x-2 pb-2 px-1" style={{ scrollbarWidth: 'none' }}>
                    {categories?.map((cat, index) => {
                      const isSelected = selectedCategoryId === cat.id;
                      
                      // Subtle pastel colors for modern theme (less saturated)
                      const modernColors = [
                        { bg: '#1E8E5A', light: '#ECFDF5', border: 'var(--status-ok-border)' }, // Emerald (subtle)
                        { bg: '#8A5411', light: '#FFFBEB', border: 'var(--status-warn-border)' }, // Amber (subtle)
                        { bg: '#D2453C', light: '#FEF2F2', border: 'var(--status-bad-border)' }, // Red (subtle)
                        { bg: '#6D5BD0', light: '#F5F3FF', border: '#DDD6FE' }, // Purple (subtle) /* basil-todo */
                        { bg: '#DB2777', light: '#FDF2F8', border: '#FBCFE8' }, // Pink (subtle) /* basil-todo */
                        { bg: '#0891B2', light: '#ECFEFF', border: '#A5F3FC' }, // Cyan (subtle) /* basil-todo */
                        { bg: '#E0891E', light: '#FFF7ED', border: 'var(--status-warn-border)' }, // Orange (subtle)
                      ];
                      const colorIndex = index % modernColors.length;
                      const catColor = modernColors[colorIndex];
                      
                      return (
                        <button
                          key={cat.id}
                          onClick={() => setSelectedCategoryId(cat.id)}
                          className={`flex-shrink-0 px-4 py-2.5 rounded-xl font-semibold transition-all duration-200 text-sm flex items-center gap-1.5`}
                          style={isModernTheme ? {
                            backgroundColor: isSelected ? catColor.light : '#FFFFFF',
                            color: catColor.bg,
                            border: isSelected ? `2px solid ${catColor.bg}` : `1px solid ${catColor.border}`,
                            boxShadow: isSelected ? `0 2px 8px ${catColor.bg}20` : 'none',
                          } : {
                            backgroundColor: isSelected ? theme.button.secondary.bg : theme.components?.card?.background || '#ffffff',
                            color: isSelected ? theme.button.secondary.text : theme.text,
                            border: isSelected ? `2px solid ${theme.button.primary.bg}` : `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                            boxShadow: isSelected ? `0 1px 3px ${theme.button.secondary.hover}` : '0 1px 2px rgba(0, 0, 0, 0.02)'
                          }}
                        >
                          {isModernTheme && <span>{getCategoryEmoji(cat.name)}</span>}
                          {cat.name}
                        </button>
                      );
                    })}
                  </div>
                </WindowsScrollArrows>

                {/* Add Category Button for Mobile - Displayed below the horizontal categories */}
                {onAddCategory && !props.isCustomer && (
                  <div className="w-full px-2 mt-2 mobile-add-category-container">
                    <button
                      onClick={() => {
                        setNewCategory({ name: '', description: '' });
                        setAddCategoryModalOpen(true);
                      }}
                      className="w-full font-medium rounded-lg py-2.5 text-center flex items-center justify-center transition-all duration-150"
                      style={{
                        backgroundColor: theme.primary,
                        color: 'var(--text-inverse)',
                        border: `1px solid ${theme.primary}`
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.filter = 'brightness(0.95)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.filter = 'none';
                      }}
                    >
                      + Add Category
                    </button>
                  </div>
                )}
              </div>

              {/* Categories - Sidebar for Desktop */}
              <aside className="hidden md:block md:w-64 lg:w-72 rounded-xl flex flex-col overflow-y-auto h-full"
                     style={{ backgroundColor: theme.components.card.background }}>
                <div className="flex flex-col h-full">
                  <div className="flex-1 overflow-y-auto p-5">
                    <h2 className="text-xl font-semibold mb-4" style={{ color: theme.text }}>{sidebarTitle}</h2>
                    <nav className="flex flex-col gap-2">
                      {categories?.map((cat, index) => {
                        const isSelected = selectedCategoryId === cat.id;

                        // Subtle pastel colors for modern theme (less saturated)
                        const modernColors = [
                          { bg: '#1E8E5A', light: '#ECFDF5', border: 'var(--status-ok-border)' }, // Emerald (subtle)
                          { bg: '#8A5411', light: '#FFFBEB', border: 'var(--status-warn-border)' }, // Amber (subtle)
                          { bg: '#D2453C', light: '#FEF2F2', border: 'var(--status-bad-border)' }, // Red (subtle)
                          { bg: '#6D5BD0', light: '#F5F3FF', border: '#DDD6FE' }, // Purple (subtle) /* basil-todo */
                          { bg: '#DB2777', light: '#FDF2F8', border: '#FBCFE8' }, // Pink (subtle) /* basil-todo */
                          { bg: '#0891B2', light: '#ECFEFF', border: '#A5F3FC' }, // Cyan (subtle) /* basil-todo */
                          { bg: '#E0891E', light: '#FFF7ED', border: 'var(--status-warn-border)' }, // Orange (subtle)
                        ];
                        const colorIndex = index % modernColors.length;
                        const catColor = modernColors[colorIndex];

                        return (
                          <button
                            key={cat.id}
                            onClick={() => setSelectedCategoryId(cat.id)}
                            className={`w-full text-left px-4 py-2.5 rounded-xl font-semibold transition-all duration-150 group flex items-center gap-2`}
                            style={isModernTheme ? {
                              backgroundColor: isSelected ? catColor.light : '#FFFFFF',
                              color: catColor.bg,
                              border: isSelected ? `2px solid ${catColor.bg}` : `1px solid ${catColor.border}`,
                              boxShadow: isSelected ? `0 2px 8px ${catColor.bg}20` : 'none',
                            } : {
                              backgroundColor: isSelected ? theme.button.secondary.bg : theme.components?.card?.background || '#ffffff',
                              color: isSelected ? theme.button.secondary.text : theme.text,
                              border: isSelected ? `2px solid ${theme.button.primary.bg}` : `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                              boxShadow: isSelected ? `0 1px 3px ${theme.button.secondary.hover}` : '0 1px 2px rgba(0, 0, 0, 0.02)'
                            }}
                          >
                            {isModernTheme && <span className="text-lg">{getCategoryEmoji(cat.name)}</span>}
                            <span className="block break-words whitespace-normal">{cat.name}</span>
                          </button>
                        );
                      })}
                    </nav>
                  </div>
                  {onAddCategory && !props.isCustomer && (
                    <div className="border-t px-5 pb-5" style={{ backgroundColor: theme.components.card.background }}>
                      <button
                        onClick={() => {
                          setNewCategory({ name: '', description: '' });
                          setAddCategoryModalOpen(true);
                        }}
                        className="w-full mt-2 px-4 py-2.5 rounded-lg transition-all duration-150 font-medium text-center"
                        style={{
                          backgroundColor: theme.primary,
                          color: 'var(--text-inverse)',
                          border: `1px solid ${theme.primary}`
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.filter = 'brightness(0.95)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.filter = 'none';
                        }}
                      >
                        + Add Category
                      </button>
                    </div>
                  )}
                </div>
              </aside>
            </>
          )}

          {/* Main Content Area */}
          <main className={`flex-1 flex flex-col h-full relative overflow-hidden tablet-card-layout ${hideSidebar ? 'w-full' : ''}`}>
            {hideSidebar ? (
              <div className="w-full h-full">
                {renderItems(categories[0])}
              </div>
            ) : selectedCategory ? (
              <div className="shadow-lg rounded-xl flex-1 flex flex-col overflow-hidden w-full max-w-[600px] mx-auto md:max-w-none md:mx-0"
                   style={{ backgroundColor: theme.components.card.background }}>
                <div className="p-4 sm:p-6 flex-1 overflow-y-auto">
                  {renderItems(selectedCategory)}
                </div>
                {onAddItemClick && !props.isCustomer && (
                  <div className="pt-4 border-t p-6 flex justify-end">
                    <button
                      className="px-4 py-2.5 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-150"
                      style={{
                        backgroundColor: theme.primary,
                        color: 'var(--text-inverse)',
                        border: `1px solid ${theme.primary}`
                      }}
                      onMouseOver={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(0.95)';
                      }}
                      onMouseOut={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.filter = 'none';
                      }}
                      onClick={() => onAddItemClick(selectedCategory.id)}
                    >
                      + Add Item
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="shadow-lg rounded-xl p-8 text-center flex-1 overflow-y-auto min-h-0"
                   style={{ backgroundColor: theme.components.card.background }}>
                <p className="text-xl" style={{ color: theme.text }}>
                  {categories?.length > 0 ? "Select a category to view items." : "No categories available."}
                </p>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Shared Add Category Modal for both mobile and desktop */}
      <Modal isOpen={isAddCategoryModalOpen} onClose={() => { setAddCategoryModalOpen(false); setNewCategory({ name: '', description: '' }); setCategoryImageKey(null); setCategoryImageUrl(null); }}>
        <h2 className="text-xl font-semibold mb-4" style={{ color: theme.text }}>Add Category</h2>
        <input
          className="w-full rounded border px-4 py-2 mb-2"
          style={{
            backgroundColor: theme.components.input.background,
            borderColor: theme.components.input.border,
            color: theme.text,
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = theme.components.input.focus;
          }}
          placeholder="Category Name"
          value={newCategory.name}
          onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
        />
        <textarea
          className="w-full rounded border px-4 py-2 mb-4"
          style={{
            backgroundColor: theme.components.input.background,
            borderColor: theme.components.input.border,
            color: theme.text,
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = theme.components.input.focus;
          }}
          placeholder="Category Description"
          value={newCategory.description}
          onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
        />
        {/* Category Image Upload */}
        {vendorId && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
              Category Image (Optional)
            </label>
            <MenuItemImageUpload
              vendorId={vendorId}
              type="categories"
              entityId={newCategory.name || 'new'}
              imageUrl={categoryImageUrl}
              imageKey={categoryImageKey}
              onImageUploaded={handleCategoryImageUploaded}
              onImageRemoved={handleCategoryImageRemoved}
            />
          </div>
        )}
        <div className="flex justify-end space-x-2">
          <button
            className="px-6 py-3 rounded-lg font-semibold transition-all"
            style={{
              backgroundColor: theme.components?.card?.background || 'white',
              color: theme.gray?.[500] || '#6b7280',
              borderWidth: '1.5px',
              borderStyle: 'solid',
              borderColor: theme.components?.card?.border || '#d1d5db',
              fontWeight: 600
            }}
            onClick={() => { setAddCategoryModalOpen(false); setCategoryImageKey(null); setCategoryImageUrl(null); }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.gray?.[50] || '#f9fafb';
              e.currentTarget.style.color = theme.text;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.components?.card?.background || 'white';
              e.currentTarget.style.color = theme.gray?.[500] || '#6b7280';
            }}
          >
            Cancel
          </button>
          <button
            className="px-6 py-3 rounded-lg font-semibold transition-all"
            style={{
              backgroundColor: theme.button.primary.bg,
              color: theme.button.primary.text,
              borderWidth: '1.5px',
              borderStyle: 'solid',
              borderColor: theme.button.primary.hover,
              fontWeight: 600,
              boxShadow: 'none'
            }}
            onClick={() => {
              handleAddCategory(newCategory);
              setAddCategoryModalOpen(false);
              setNewCategory({ name: '', description: '' });
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.button.primary.hover;
              e.currentTarget.style.borderColor = theme.button.primary.bg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.button.primary.bg;
              e.currentTarget.style.borderColor = theme.button.primary.hover;
            }}
          >
            Add
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default CardLayoutTheme;
